import * as React from 'react';
import { useCallback } from 'react';

import { useMemoizedState } from '@/lib/react-hooks';
import { useViewport } from '@/lib/layout/dimensions';

import { InfiniteList, InfiniteListProps } from '../shared/InfiniteList';
import { TicketSkeletonLoader } from '../ui-elements/ui-skeleton';
import { TicketCard } from './TicketCard';

export interface TicketListProps {
  uuids: string[];
  hasNextPage: InfiniteListProps['hasNextPage'];
  isNextPageLoading: InfiniteListProps['isNextPageLoading'];
  loadNextPage: InfiniteListProps['loadNextPage'];
  activeUuid?: string;
  wide?: boolean;
  compact?: boolean;
}

export const TicketList = ({
  uuids, activeUuid, compact, wide,
  hasNextPage, isNextPageLoading, loadNextPage,
}: TicketListProps) => {
  const [expandedUuid, setExpandedUuid] = useMemoizedState<string>(null);

  const handleTicketMouseEnter = useCallback((event) => {
    setExpandedUuid(event.currentTarget?.id);
  }, [setExpandedUuid]);

  const handleTicketMouseLeave = useCallback((event) => {
    event.persist();
    setExpandedUuid((uuid) => (event.currentTarget?.id === uuid ? null : uuid));
  }, [setExpandedUuid]);

  const renderItem = useCallback((uuid) => (
    <TicketCard
      key={uuid}
      uuid={uuid}
      active={activeUuid === uuid}
      onMouseEnter={compact ? handleTicketMouseEnter : null}
      onMouseLeave={compact ? handleTicketMouseLeave : null}
    />
  ), [activeUuid, compact, handleTicketMouseEnter, handleTicketMouseLeave]);

  /* eslint-disable no-nested-ternary */
  const mobile = useViewport((width) => width < 600, 500);
  const collapsedTicketHeight = mobile ? (wide ? 106 : 55) : (wide ? 23 : 55);
  const compactTicketHeight = mobile ? 106 : (wide ? 23 : 165);
  const fullTicketHeight = mobile ? 225 : (wide ? 33 : 225);
  /* eslint-enable no-nested-ternary */

  const getRowHeight = useCallback<InfiniteListProps['rowHeightFn']>((ix) => (
    uuids[ix] === activeUuid || uuids[ix] === expandedUuid
      ? compactTicketHeight
      : collapsedTicketHeight
  ), [uuids, activeUuid, expandedUuid]);

  const renderEmptyScreen = () => {
    if (uuids.length === 0 && !isNextPageLoading) {
      return (
        <div
          className="final-box"
          style={{
            position: 'absolute',
            width: '100%',
          }}
        >
          <p>No more conversations to show</p>
        </div>
      );
    }
  };

  return (
    <div className="tickets" id="sticky-inbox" data-turbolinks-permanent>
      <div className="start-time hide">{/* <%= @started_at %> */}</div>
      <div className="current-page hide">1</div>
      <InfiniteList
        list={uuids}
        renderItem={renderItem}
        hasNextPage={hasNextPage}
        isNextPageLoading={isNextPageLoading}
        loadNextPage={loadNextPage}
        loader={<TicketSkeletonLoader rows={5} />}
        estimatedRowHeight={compact ? collapsedTicketHeight : fullTicketHeight}
        rowHeightFn={compact ? getRowHeight : null}
        loaderHeight={50}
        paginationThreshold={10}
        containerClassName="container"
      />
      {renderEmptyScreen()}
    </div>
  );
};
